import { Component, createRef } from 'react'
import clx from 'classnames'
import _ from 'lodash'
import { Table } from 'antd'
import {
  sortableContainer,
  sortableElement,
  sortableHandle
} from 'react-sortable-hoc'
import arrayMove from 'array-move'
import { delay } from '../../../../lib/renderUtil'
import styles from './SortableTable.css'

export const DragHandler = sortableHandle(({ disabled, className, ...props }) => (
  <div
    className={clx(
      styles.dragHandlerArea,
      { [className]: className },
      { [styles.disabled]: disabled }
    )}
    {...props}
  >
    <span className={styles.sortableIcon} />
  </div>
))

const SortableItem = sortableElement((props) => <tr {...props} />)
const SortableContainer = sortableContainer((props) => <tbody {...props} />)

const DraggableContainer = (props) => {
  const {
    handleSortEnd,
    handleSortStart,
    container,
    useWindowAsScrollContainer = true,
    ...restProps
  } = props
  return (
    <SortableContainer
      lockAxis='y'
      helperClass={clx(
        'sortableHelper',
        { [container]: container }
      )}
      onSortEnd={handleSortEnd}
      onSortStart={handleSortStart}
      useWindowAsScrollContainer={useWindowAsScrollContainer}
      useDragHandle
      lockToContainerEdges
      {...restProps}
    />
  )
}

class SortableTable extends Component {
  constructor (props) {
    super(props)
    this.state = {
      dataSource: props.data,
      currentCollect: null,
      sortableAreaStyles: null,
      ignoreSortingAnimation: false
    }
    this.components = {
      body: {
        wrapper: props => (
          <DraggableContainer
            handleSortStart={this.handleSortStart}
            handleSortEnd={this.handleSortEnd}
            {..._.pick(
              this.props,
              ['container', 'useWindowAsScrollContainer', 'getContainer']
            )}
            {...props}
          />
        ),
        row: this.DraggableBodyRow
      }
    }
    this.sortTableArea = createRef()
    this.confirmAction = null
  }

  componentDidUpdate (prevProps, prevState) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.resetDataSource()
    }
  }

  handleSortStart = (props) => {
    const { node } = props
    const { collection } = _.get(node, 'sortableInfo', {})
    const withoutCollection = _.isUndefined(collection) || (collection === 0)
    const tds = _.get(
      // sortableHelper classname 只有在拖曳項目時會加到 react-sortable-hoc clone 的節點上
      // 因此可以直接搜尋 classname，同時不可能有多個項目正在拖曳
      document.querySelectorAll('.sortableHelper'),
      '[0].childNodes'
    )
    if (_.isEmpty(tds)) {
      return
    }
    // clone 出來的節點會以 portal 的方式直接放在 document 底下因此對不到原本的 class style
    // 所以會用 react-sortable-hoc 傳入的原本節點調整樣式
    node.childNodes.forEach(
      (node, idx) => (tds[idx].style.width = `${node.offsetWidth}px`)
    )

    if (!withoutCollection) {
      this.setState(
        () => ({ currentCollect: collection }),
        () => this.setSortableAreaStyles()
      )
    }
  }

  setSortableAreaStyles = () => {
    // 拖曳中屬於同一個 collection 的項目會加上 sortingItem classname
    // 方便辨識可以交換位置的項目
    const sortingItemElements = document.querySelectorAll('.sortingItem')
    // 沒有拖曳中的項目則不需要顯示可拖曳範圍
    if (_.size(sortingItemElements) === 0) {
      return
    }
    const sortingItemPositions = [...sortingItemElements].map(ele => ele.getBoundingClientRect())
    const sortTableAreaPosition = this.sortTableArea.current.getBoundingClientRect()
    const [
      firstItemPosition = {},
      lastItemPosition = {}
    ] = [_.first(sortingItemPositions), _.last(sortingItemPositions)]
    const commonOffset = 4
    const sortableAreaXOffset = firstItemPosition.left - sortTableAreaPosition.left - commonOffset
    const sortableAreaYOffset = firstItemPosition.top - sortTableAreaPosition.top - commonOffset
    const sortableAreaStyles = {
      transform: `translate(${sortableAreaXOffset}px, ${sortableAreaYOffset}px)`,
      width: firstItemPosition.width + (commonOffset * 2),
      height: lastItemPosition.bottom - firstItemPosition.top + (commonOffset * 2)
    }
    this.setState({ sortableAreaStyles })
  }

  handleSortEnd = async (props) => {
    const { onSortEnd } = this.props
    const { dataSource: prevDataSource } = this.state
    const { oldIndex, newIndex } = props
    let newDataSource = []
    if (oldIndex !== newIndex) {
      newDataSource = arrayMove(prevDataSource, oldIndex, newIndex)
      this.setState({ dataSource: newDataSource })
      // 重置這次排序時可以使用
      this.cancelAction = () => this.setState({ dataSource: prevDataSource })
    }
    this.setState({
      currentCollect: null,
      sortableAreaStyles: null,
      ignoreSortingAnimation: true
    })
    await delay(100)
    this.setState(
      () => ({ ignoreSortingAnimation: false }),
      () => this.executeOrStoreAction(
        () => {
          const { checkToStoreAction } = this.props
          if (!checkToStoreAction) {
            return () => false
          }
          return checkToStoreAction(props)
        },
        () => {
          if (onSortEnd && !_.isEmpty(newDataSource)) {
            onSortEnd(newDataSource)
          }
        }
      )
    )
  }

  executeOrStoreAction = (checkIsStoreAction, cb) => {
    if (!checkIsStoreAction()) {
      cb()
      return
    }
    this.confirmAction = cb
  }

  executeConfirmAction = () => {
    this.confirmAction && this.confirmAction()
    this.resetExecuteAction()
  }

  executeCancelAction = () => {
    this.cancelAction && this.cancelAction()
    this.resetExecuteAction()
  }

  resetExecuteAction = () => {
    this.confirmAction = null
    this.cancelAction = null
  }

  resetDataSource = () => this.setState({ dataSource: this.props.data })

  DraggableBodyRow = ({ className, style, ...restProps }) => {
    const { dataSource } = this.state
    const index = dataSource.findIndex(
      (x) => x.index === restProps['data-row-key']
    )
    const currentCollect = _.get(dataSource, `${index}.collection`, 0)
    const isSortingItem = this.state.currentCollect === currentCollect
    return (
      <SortableItem
        index={index}
        collection={currentCollect}
        className={clx(
          className,
          { sortingItem: isSortingItem }
        )}
        {...restProps}
      />
    )
  }

  render () {
    const {
      rowKey = 'index',
      columns,
      noLastBorder = false,
      className,
      ...restProps
    } = this.props
    const {
      dataSource,
      sortableAreaStyles,
      ignoreSortingAnimation
    } = this.state

    return (
      <div
        className={clx(
          styles.sortableTable,
          { [styles.ignoreSortingAnimation]: ignoreSortingAnimation }
        )}
        ref={this.sortTableArea}
      >
        <div
          className={clx(
            styles.sortableArea,
            { [styles.hidden]: _.isEmpty(sortableAreaStyles) }
          )}
          style={sortableAreaStyles}
        />
        <Table
          rowKey={rowKey}
          className={clx(
            { [className]: className },
            { [styles.noLastBorder]: noLastBorder }
          )}
          dataSource={dataSource}
          columns={columns}
          components={this.components}
          pagination={false}
          {...restProps}
        />
      </div>
    )
  }
}

export default SortableTable
